/**
 * BenchBoardsScreen — the per-discipline bench boards + the scan→discipline-routing panel.
 *
 *  - Reads GET /sampling/files-attente (the lab's FileAttente benches, one per discipline).
 *  - Scan a tube (POST /sampling/scan, sampling.prelevement.read) -> shows its discipline set, then
 *    fan it into its per-discipline benches (POST /sampling/tubes/:id/enfiler,
 *    sampling.prelevement.create). Offline mode enqueues the primary discipline only.
 *  - Full 5-state DoD (offline/loading/error/empty + route-level forbidden).
 *  - Keyboard-first: the scan field submits on Enter.
 */
import { useState } from "react";
import "../sampling.css";
import {
  Badge,
  Button,
  Card,
  ErrorState,
  FormField,
  Input,
  OfflineState,
  Table,
  useToast,
} from "@/ui";
import type { Column } from "@/ui";
import { useT, useUiStore } from "@/app";
import { useCan } from "@/auth";
import { type ApiError } from "@/api";
import { token } from "@/design/theme";
import { CAP } from "../capabilities";
import { useBenchFiles, useEnqueueTube, useScan } from "../hooks";
import type { BenchFile, Tube } from "../types";

function ScanPanel() {
  const { t } = useT();
  const toast = useToast();
  const online = useUiStore((s) => s.online);
  const canScan = useCan(CAP.prelevementRead);
  const canEnqueue = useCan(CAP.prelevementCreate);

  const [input, setInput] = useState("");
  const [tube, setTube] = useState<Tube | null>(null);

  const scan = useScan();
  const enqueue = useEnqueueTube();

  if (!canScan) return null;

  const resolve = () => {
    const value = input.trim();
    if (!value) return;
    // A raw QR token and a matricule travel the same field; send matricule (the human-typed path).
    scan.mutate(
      { matricule: value },
      {
        onSuccess: (resolved) => {
          setTube(resolved);
          toast.success(t("sampling.scan.resolved", { m: resolved.matricule ?? resolved.id }));
        },
        onError: () => toast.error(t("sampling.scan.notResolved")),
      },
    );
  };

  const doEnqueue = () => {
    if (!tube) return;
    enqueue.mutate(
      { tubeId: tube.id, offline: !online },
      {
        onSuccess: (res) => {
          toast.success(t("sampling.scan.enqueued", { n: res.entries.length }));
          setTube(null);
          setInput("");
        },
        onError: (e: ApiError) => toast.error(e.message, e.code),
      },
    );
  };

  return (
    <Card title={t("sampling.scan.title")}>
      <div className="smp-scan">
        <div className="smp-scan__row">
          <FormField label={t("sampling.scan.input")} className="smp-grow">
            {(p) => (
              <Input
                {...p}
                mono
                value={input}
                placeholder={t("sampling.scan.inputPlaceholder")}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    resolve();
                  }
                }}
              />
            )}
          </FormField>
          <Button variant="secondary" onClick={resolve} loading={scan.isPending} disabled={!input.trim()}>
            {t("sampling.scan.resolve")}
          </Button>
        </div>

        {tube && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: token.s3,
              border: `1px solid ${token.line}`,
              borderRadius: token.rMd,
              padding: token.s3,
            }}
          >
            <div style={{ display: "flex", alignItems: "baseline", gap: token.s2 }}>
              <strong className="nv-mono nv-ltr-island">{tube.matricule ?? tube.id}</strong>
              <Badge tone="info">{t(`sampling.statut.${tube.statut}`)}</Badge>
            </div>
            <div>
              <span style={{ color: token.slate, marginInlineEnd: token.s2 }}>
                {t("sampling.scan.disciplines")}
              </span>
              <span className="smp-disc-chips">
                {(tube.discipline_codes ?? []).map((d) => (
                  <Badge key={d} tone="neutral">
                    <span className="nv-mono nv-ltr-island">{d}</span>
                  </Badge>
                ))}
              </span>
            </div>
            {!online && (
              <span style={{ color: token.warn, fontSize: token.fsCaption }}>
                ⚠ {t("sampling.scan.offline")}
              </span>
            )}
            <div className="smp-actions">
              <Button variant="ghost" onClick={() => setTube(null)}>
                {t("sampling.scan.reset")}
              </Button>
              {canEnqueue && (
                <Button variant="primary" onClick={doEnqueue} loading={enqueue.isPending}>
                  {t("sampling.scan.enqueue")}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

export function BenchBoardsScreen() {
  const { t } = useT();
  const online = useUiStore((s) => s.online);
  const benches = useBenchFiles();

  const columns: ReadonlyArray<Column<BenchFile>> = [
    {
      key: "discipline",
      header: t("sampling.benches.discipline"),
      mono: true,
      sortable: true,
      sortAccessor: (r) => r.discipline_code,
      cell: (r) => <span className="nv-mono nv-ltr-island">{r.discipline_code}</span>,
    },
    {
      key: "nom",
      header: t("sampling.benches.name"),
      cell: (r) => r.nom,
    },
    {
      key: "actif",
      header: t("sampling.benches.active"),
      cell: (r) =>
        r.actif ? (
          <Badge tone="success">{t("sampling.benches.active")}</Badge>
        ) : (
          <Badge tone="neutral">{t("sampling.benches.inactive")}</Badge>
        ),
    },
  ];

  if (!online) return <OfflineState onRetry={() => void benches.refetch()} cached={!!benches.data} />;

  if (benches.isError && !benches.data) {
    return (
      <ErrorState
        message={benches.error.message}
        code={benches.error.code}
        onRetry={() => void benches.refetch()}
      />
    );
  }

  return (
    <div className="smp-page">
      <header className="smp-head">
        <h1 className="smp-head__title">{t("sampling.benches.title")}</h1>
      </header>

      <ScanPanel />

      <Table<BenchFile>
        columns={columns}
        rows={benches.data ?? []}
        rowKey={(r) => r.id}
        caption={t("sampling.benches.title")}
        loading={benches.isLoading}
        emptyTitle={t("sampling.benches.empty")}
        emptyDescription={t("sampling.benches.emptyDesc")}
      />
    </div>
  );
}
